import { useEffect, useMemo, useState } from 'react'
import { Control, FieldValues, Path, useController } from 'react-hook-form'
import debounce from 'lodash/debounce'
import { NoImageIcon } from 'components/icons'

type Props<T extends FieldValues> = {
  name: Path<T>
  control?: Control<T>
  label?: string
  error?: string
}

/**
 * Paste-a-URL image field for the Create Bundle Product form. A dedicated
 * component (rather than reusing the shared ImagePreviewField, which other
 * forms already depend on) so the bundle-product create page can have its
 * own large, well-proportioned preview card without affecting anywhere else
 * that field is used.
 */
const BundleImageField = <T extends FieldValues>({
  name,
  control,
  label = 'Product Image',
  error: externalError,
}: Props<T>) => {
  const {
    field: { value, onChange, ref },
    fieldState: { error: internalError },
  } = useController({ name, control })

  const [inputValue, setInputValue] = useState(value || '')
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    setInputValue(value || '')
  }, [value])

  const debouncedUpdate = useMemo(
    () =>
      debounce((val: string) => {
        onChange(val)
        setIsLoading(Boolean(val))
      }, 600),
    [onChange],
  )

  useEffect(() => () => debouncedUpdate.cancel(), [debouncedUpdate])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    setInputValue(val)
    debouncedUpdate(val)
  }

  const handleLoad = () => {
    setIsLoading(false)
    control?.setError(name, { message: '' })
  }

  const handleError = () => {
    setIsLoading(false)
    if (value) {
      control?.setError(name, {
        type: 'manual',
        message: 'Valid URL format, but the image failed to load.',
      })
    }
  }

  const errorMessage = internalError?.message || externalError
  const showPreview = Boolean(value) && !errorMessage

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
      <div
        className={`relative flex h-32 w-32 shrink-0 items-center justify-center overflow-hidden rounded-2xl border bg-gray-50/60 ${
          errorMessage ? 'border-error/40' : 'border-gray-100'
        }`}
      >
        {showPreview ? (
          <>
            {isLoading && (
              <div className="absolute inset-0 animate-pulse bg-gray-200" />
            )}
            <img
              key={value}
              src={value}
              onLoad={handleLoad}
              onError={handleError}
              alt="Product preview"
              className={`h-full w-full object-contain p-2 transition-opacity ${
                isLoading ? 'opacity-0' : 'opacity-100'
              }`}
            />
          </>
        ) : (
          <div className="flex flex-col items-center gap-1.5 text-gray-300">
            <span className="h-7 w-7">
              <NoImageIcon />
            </span>
            <span className="text-[11px] font-medium text-gray-400">
              No image
            </span>
          </div>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <label
          htmlFor={String(name)}
          className="mb-1 block text-xs font-semibold text-sidebar-primary"
        >
          {label}
        </label>
        <div
          className={`flex h-11 w-full items-center rounded-lg border bg-white px-3 focus-within:border-2 ${
            errorMessage ? 'border-error' : 'border-gray-300'
          }`}
        >
          <input
            id={String(name)}
            type="text"
            placeholder="Paste an image URL"
            ref={ref}
            value={inputValue}
            onChange={handleChange}
            className="h-full w-full bg-transparent text-sm text-primary outline-none placeholder:text-gray-400"
          />
        </div>
        <p className="mt-1 text-[11px] text-gray-400">
          Paste a direct link to an image — a preview appears automatically.
        </p>
        {errorMessage && (
          <p className="mt-1 text-xs font-medium text-error">{errorMessage}</p>
        )}
      </div>
    </div>
  )
}

export default BundleImageField
